(() => {
  if (window.productSectionZoomInitialized) return;
  window.productSectionZoomInitialized = true;
  const controllers = new Map();
  let serial = 0;
  const icon = paths => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths}</svg>`;

  class ProductSectionZoom {
    constructor(top, media, source) {
      this.top = top;
      this.media = media;
      this.source = source;
      this.cleanups = [];
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.mobile = window.matchMedia('(max-width: 767px)');
      this.trigger = document.createElement('button');
      this.trigger.type = 'button';
      this.trigger.className = 'product-image-zoom__trigger';
      this.trigger.setAttribute('aria-label', 'Enlarge product image');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.innerHTML = icon('<circle cx="10.5" cy="10.5" r="6.5"/><path d="m16 16 5 5M7.5 10.5h6M10.5 7.5v6"/>');
      this.viewer = document.createElement('div');
      this.viewer.className = 'product-image-zoom';
      this.viewer.id = `product-image-zoom-${++serial}`;
      this.viewer.hidden = true;
      this.viewer.setAttribute('role', 'dialog');
      this.viewer.setAttribute('aria-label', 'Enlarged product image');
      this.trigger.setAttribute('aria-controls', this.viewer.id);
      this.viewer.innerHTML = '<div class="product-image-zoom__backdrop"></div><div class="product-image-zoom__stage"><img alt="" draggable="false"></div><button type="button" class="product-image-zoom__close" aria-label="Close enlarged image"></button>';
      this.stage = this.viewer.querySelector('.product-image-zoom__stage');
      this.backdrop = this.viewer.querySelector('.product-image-zoom__backdrop');
      this.image = this.stage.querySelector('img');
      this.closeButton = this.viewer.querySelector('button');
      this.closeButton.innerHTML = icon('<path d="M6 6L18 18M18 6L6 18"/>');
      top.append(this.trigger, this.viewer);
      top.setAttribute('data-product-image-zoom', '');
      this.listen(this.mobile, 'change', () => {
        if (this.mobile.matches && this.open) {
          this.setOpen(false);
          this.finishMotion();
        }
        this.trigger.hidden = this.mobile.matches || !!this.open;
      });
      this.trigger.hidden = this.mobile.matches;
      this.listen(this.trigger, 'click', () => this.setOpen(true));
      // Capture above Pixify's media listener, avoiding two competing zooms.
      this.listen(top, 'click', event => {
        if (event.target === this.source) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.setOpen(true);
        }
      }, true);
      this.listen(this.viewer, 'click', event => {
        if (this.suppressClick && !event.target.closest('button')) {
          this.suppressClick = false;
          return;
        }
        this.setOpen(false);
      });
      this.listen(this.viewer, 'pointerdown', event => {
        if (event.pointerType !== 'touch' || event.isPrimary === false || this.touchPan || event.target.closest('button') || !this.open || this.motion) return;
        this.suppressClick = false;
        this.flushPan();
        const rect = this.viewer.getBoundingClientRect();
        const width = this.source.naturalWidth || this.source.width;
        const height = this.source.naturalHeight || this.source.height;
        if (!width || !height || !rect.width || !rect.height) return;
        const scale = Math.max(rect.width / width, rect.height / height);
        const position = this.image.style.objectPosition.split(' ').map(Number.parseFloat);
        this.touchPan = {
          id: event.pointerId,
          x: event.clientX, y: event.clientY,
          startX: event.clientX, startY: event.clientY,
          positionX: Number.isFinite(position[0]) ? position[0] : 50,
          positionY: Number.isFinite(position[1]) ? position[1] : 50,
          overflowX: Math.max(0, width * scale - rect.width),
          overflowY: Math.max(0, height * scale - rect.height)
        };
        this.viewer.setPointerCapture?.(event.pointerId);
      });
      for (const type of ['pointerup', 'pointercancel', 'lostpointercapture']) this.listen(document, type, event => {
        if (this.touchPan?.id !== event.pointerId) return;
        if (type !== 'pointerup') this.suppressClick = true;
        this.endTouchPan();
      });
      this.listen(document, 'keydown', event => {
        if (event.key === 'Escape' && this.open) {
          event.preventDefault();
          this.setOpen(false);
        }
      });
      this.listen(this.viewer, 'pointermove', event => {
        if (!this.open || this.motion) return;
        if (event.pointerType === 'touch' && this.touchPan) {
          const pan = this.touchPan;
          if (pan.id !== event.pointerId) return;
          if (Math.hypot(event.clientX - pan.startX, event.clientY - pan.startY) > 8) this.suppressClick = true;
          // Incremental movement makes reversing direction at an edge immediate.
          if (pan.overflowX > .5) pan.positionX = Math.max(0, Math.min(100, pan.positionX - (event.clientX - pan.x) / pan.overflowX * 100));
          if (pan.overflowY > .5) pan.positionY = Math.max(0, Math.min(100, pan.positionY - (event.clientY - pan.y) / pan.overflowY * 100));
          pan.x = event.clientX;
          pan.y = event.clientY;
          this.queuePan(pan.positionX, pan.positionY);
          return;
        }
        if (!this.finePointer.matches || event.pointerType === 'touch') return;
        const rect = this.viewer.getBoundingClientRect();
        const x = Math.max(0, Math.min(100, (event.clientX - rect.left) / rect.width * 100));
        const y = Math.max(0, Math.min(100, (event.clientY - rect.top) / rect.height * 100));
        this.image.style.objectPosition = `${x}% ${y}%`;
      });
      this.listen(source, 'load', () => { this.syncSource(); this.schedulePosition(); });
      this.listen(window, 'resize', () => {
        this.endTouchPan();
        this.finishMotion();
        this.schedulePosition();
      });
      this.resize = new ResizeObserver(() => this.schedulePosition());
      this.resize.observe(top);
      this.resize.observe(source);
      this.attributes = new MutationObserver(() => { this.syncSource(); this.schedulePosition(); });
      this.attributes.observe(source, {attributes: true, attributeFilter: ['src', 'srcset', 'sizes']});
      this.attributes.observe(top, {attributes: true, attributeFilter: ['style', 'data-desktop-content-aligned']});
      this.schedulePosition();
    }

    listen(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      this.cleanups.push(() => target.removeEventListener(type, handler, options));
    }

    queuePan(x, y) {
      this.pendingPan = `${x}% ${y}%`;
      if (!this.panFrame) this.panFrame = requestAnimationFrame(() => this.flushPan());
    }

    flushPan() {
      cancelAnimationFrame(this.panFrame);
      this.panFrame = null;
      if (this.pendingPan != null) {
        if (this.image.style.objectPosition !== this.pendingPan) this.image.style.objectPosition = this.pendingPan;
        this.pendingPan = null;
      }
    }

    endTouchPan() {
      this.flushPan();
      const pan = this.touchPan;
      this.touchPan = null;
      if (pan && this.viewer.hasPointerCapture?.(pan.id)) this.viewer.releasePointerCapture(pan.id);
    }

    syncSource() {
      if (!this.open) return;
      // Reuse the displayed preview, including its watermark; never request an original file.
      const src = this.source.currentSrc || this.source.src;
      if (this.image.getAttribute('src') !== src) this.image.src = src;
      this.image.alt = this.source.alt;
      // Keep the cached preview visible while a higher-resolution version of
      // that same watermarked preview loads. Swap only after it is ready.
      const larger = this.source.src;
      if (larger && larger !== src) {
        if (this.largerPreview?.src === larger && this.largerPreview.complete && this.largerPreview.naturalWidth) {
          this.image.src = larger;
        } else if (this.largerPreview?.src !== larger) {
          const preview = new Image();
          this.largerPreview = preview;
          preview.onload = () => {
            if (this.open && this.source.src === larger && this.largerPreview === preview) this.image.src = larger;
          };
          preview.src = larger;
        }
      }
    }

    sourceBounds() {
      const rect = this.source.getBoundingClientRect();
      const parent = this.top.getBoundingClientRect();
      const ratio = (this.source.naturalWidth || this.source.width) / (this.source.naturalHeight || this.source.height) || 1;
      const width = Math.min(rect.width, rect.height * ratio);
      const height = Math.min(rect.height, rect.width / ratio);
      return {left: rect.left - parent.left + (rect.width - width) / 2, top: rect.top - parent.top + (rect.height - height) / 2, width, height};
    }

    schedulePosition() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (!this.source.complete || !this.source.naturalWidth) return;
        const rect = this.sourceBounds();
        if (!rect.width || !rect.height) return;
        for (const [name, value] of [['x', rect.left + rect.width - 56], ['y', rect.top + rect.height - 56]]) {
          const property = `--product-zoom-button-${name}`;
          const next = `${Math.max(4, value).toFixed(2)}px`;
          if (this.top.style.getPropertyValue(property) !== next) this.top.style.setProperty(property, next);
        }
        this.top.setAttribute('data-product-zoom-ready', '');
      });
    }

    setOpen(open) {
      if (open && this.mobile.matches) return;
      if (!!this.open === open || (open && !this.source.naturalWidth)) return;
      const wasHidden = this.viewer.hidden;
      const parent = this.top.getBoundingClientRect();
      const rect = this.stage.getBoundingClientRect();
      const from = wasHidden ? this.sourceBounds() : {left: rect.left - parent.left, top: rect.top - parent.top, width: rect.width, height: rect.height};
      const fromOpacity = wasHidden ? 0 : Number.parseFloat(getComputedStyle(this.backdrop).opacity) || 0;
      this.cancelMotion();
      this.endTouchPan();
      this.open = open;
      this.trigger.setAttribute('aria-expanded', String(open));
      if (open) {
        this.syncSource();
        this.image.style.objectPosition = '50% 50%';
        this.viewer.hidden = false;
        this.viewer.inert = false;
        if (!this.inertState) {
          this.inertState = [this.media, this.top.querySelector('.pi-product-details__details')].filter(Boolean).map(el => [el, el.inert]);
          this.inertState.forEach(([el]) => { el.inert = true; });
        }
        this.closeButton.focus({preventScroll: true});
        this.trigger.hidden = true;
      } else {
        this.viewer.inert = true;
        this.trigger.hidden = this.mobile.matches;
        if (!this.mobile.matches) this.trigger.focus({preventScroll: true});
      }
      const target = open ? {left: 0, top: 0, width: parent.width, height: parent.height} : this.sourceBounds();
      const frame = box => Object.fromEntries(Object.entries(box).map(([key, value]) => [key, `${value}px`]));
      const options = {duration: open ? 400 : 320, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'both'};
      if (this.reduced.matches || !this.stage.animate) {
        this.finishMotion();
        return;
      }
      this.motion = this.stage.animate([frame(from), frame(target)], options);
      this.fade = this.backdrop.animate([{opacity: fromOpacity}, {opacity: open ? 1 : 0}], options);
      this.motion.onfinish = () => this.finishMotion();
    }

    cancelMotion() {
      if (this.motion) this.motion.onfinish = null;
      this.motion?.cancel();
      this.fade?.cancel();
      this.motion = this.fade = null;
    }

    finishMotion() {
      this.cancelMotion();
      if (!this.open) {
        this.viewer.hidden = true;
        this.inertState?.forEach(([el, inert]) => { el.inert = inert; });
        this.inertState = null;
      }
    }

    destroy() {
      this.endTouchPan();
      this.open = false;
      this.finishMotion();
      cancelAnimationFrame(this.frame);
      this.resize.disconnect();
      this.attributes.disconnect();
      if (this.largerPreview) this.largerPreview.onload = null;
      this.cleanups.forEach(cleanup => cleanup());
      this.trigger.remove();
      this.viewer.remove();
      this.top.removeAttribute('data-product-image-zoom');
      this.top.removeAttribute('data-product-zoom-ready');
      this.top.style.removeProperty('--product-zoom-button-x');
      this.top.style.removeProperty('--product-zoom-button-y');
    }
  }

  function scan() {
    for (const [top, controller] of controllers) {
      if (!top.isConnected || !controller.source.isConnected) {
        controller.destroy();
        controllers.delete(top);
      }
    }
    document.querySelectorAll('.pi-product-details__top').forEach(top => {
      if (controllers.has(top)) return;
      const media = top.querySelector('.pi-product-details__media[data-zoom-enabled]');
      const source = media?.querySelector('.pi-product-details__media-item img');
      if (source) controllers.set(top, new ProductSectionZoom(top, media, source));
    });
  }
  let scanFrame;
  const observer = new MutationObserver(() => {
    if (scanFrame) return;
    scanFrame = requestAnimationFrame(() => { scanFrame = null; scan(); });
  });
  observer.observe(document.body, {childList: true, subtree: true});
  scan();
})();
